import { useCallback, useEffect } from 'react'

import {
  MeetingStatus,
  useAudioInputs,
  useAudioVideo,
  useLocalVideo,
  useLogger,
  useMeetingManager,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react'
import { LogLevel, MeetingSessionConfiguration } from 'amazon-chime-sdk-js'

import { useMeetingContext } from '../useMeetingContext'

type LeaveMeetingOptions = {
  reload?: boolean
}

export const useMeeting = () => {
  const meetingManager = useMeetingManager()
  const { meeting, attendee, setMeeting, setAttendee, setIsRedial } =
    useMeetingContext()

  const logger = useLogger()
  logger.setLogLevel(LogLevel.ERROR)

  const audioVideo = useAudioVideo()
  const { toggleVideo } = useLocalVideo()
  const { devices: videoDevices } = useVideoInputs()
  const { devices: audioDevices } = useAudioInputs()

  useEffect(() => {
    if (meetingManager.meetingStatus !== MeetingStatus.Succeeded) {
      return
    }

    const selectedVideoInput = localStorage.getItem('videoInput')
    const selectedAudioInput = localStorage.getItem('audioInput')

    // Check saved device is in the list of available devices
    if (
      selectedVideoInput &&
      videoDevices.find((device) => device.deviceId === selectedVideoInput)
    ) {
      meetingManager.startVideoInputDevice(selectedVideoInput)
    }
    if (
      selectedAudioInput &&
      audioDevices.find((device) => device.deviceId === selectedAudioInput)
    ) {
      meetingManager.startAudioInputDevice(selectedAudioInput)
    }
  }, [meetingManager, meetingManager.meetingStatus, videoDevices, audioDevices])

  const leaveMeeting = useCallback(
    async ({ reload = true }: LeaveMeetingOptions = {}) => {
      await meetingManager.leave()
      setMeeting(null)
      setAttendee(null)
      setIsRedial(false)
      if (reload) {
        window.location.reload()
      }
    },
    [meetingManager, setMeeting, setAttendee, setIsRedial]
  )

  useEffect(() => {
    async function startMeeting() {
      if (meeting && attendee) {
        const configuration = new MeetingSessionConfiguration(meeting, attendee)
        await meetingManager.join(configuration)
        await meetingManager.start()
      }
    }

    startMeeting()
  }, [meeting, attendee, meetingManager])

  useEffect(() => {
    // Start local video when enabled audioVideo
    toggleVideo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioVideo])

  return {
    leaveMeeting,
    meeting,
    attendee,
  }
}
